import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const scanline = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  50% { opacity: 0.3; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const dataFlow = keyframes`
  0% { transform: translateX(-100%) rotate(0deg); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateX(100%) rotate(180deg); opacity: 0; }
`;

const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px) translateZ(0);
  }
  50% { 
    transform: translateY(-15px) translateZ(10px);
  }
`;

const shimmer = keyframes`
  0% { 
    background-position: -200% center;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% { 
    background-position: 200% center;
    opacity: 0.3;
  }
`;

const rotateGlow = keyframes`
  0% { 
    transform: rotate(0deg) scale(1);
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% { 
    transform: rotate(360deg) scale(1);
    opacity: 0.6;
  }
`;

const techGridPulse = keyframes`
  0%, 100% { 
    opacity: 0.08;
    transform: scale(1);
  }
  50% { 
    opacity: 0.2;
    transform: scale(1.01);
  }
`;

const fadeInSlide = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
    filter: blur(5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const hologramFlicker = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
  75% { opacity: 0.95; }
`;

const energyPulse = keyframes`
  0% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
  50% {
    box-shadow: 
      0 0 40px rgba(100, 182, 255, 0.4),
      inset 0 0 30px rgba(100, 182, 255, 0.2);
  }
  100% {
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.2),
      inset 0 0 20px rgba(100, 182, 255, 0.1);
  }
`;

const particleFloat = keyframes`
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    transform: translate(${Math.random() * 100 - 50}px, -150px) scale(0.3);
    opacity: 0;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 40px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  perspective: 1000px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(100, 182, 255, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 50%, rgba(0, 255, 255, 0.02) 0%, transparent 50%),
      linear-gradient(90deg, rgba(100, 182, 255, 0.02) 1px, transparent 1px),
      linear-gradient(rgba(100, 182, 255, 0.02) 1px, transparent 1px);
    background-size: 100% 100%, 100% 100%, 60px 60px, 60px 60px;
    animation: ${techGridPulse} 8s ease-in-out infinite;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, transparent 0%, rgba(0, 10, 20, 0.3) 100%);
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  background: linear-gradient(
    135deg,
    rgba(100, 182, 255, 0.9),
    rgba(0, 255, 255, 0.9),
    rgba(100, 182, 255, 0.9)
  );
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 8px;
  font-size: 2.8rem;
  text-transform: uppercase;
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  font-weight: 300;
  filter: drop-shadow(0 0 30px rgba(100, 182, 255, 0.5));
  animation: ${shimmer} 6s ease-in-out infinite, ${hologramFlicker} 4s ease-in-out infinite;
  z-index: 10;
  
  &::before {
    content: '// TRUSTED BY';
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(100, 182, 255, 0.4);
    font-weight: 300;
    font-size: 0.7rem;
    letter-spacing: 3px;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 250px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 0.8),
      rgba(100, 182, 255, 0.8),
      transparent
    );
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  }
`;

const ContentPreview = styled.div`
  max-width: 1400px;
  width: 95%;
  margin: 0 auto;
  margin-top: 100px;
  margin-bottom: 40px;
  padding: 60px 80px 60px;
  border: 1px solid rgba(100, 182, 255, 0.2);
  border-radius: 30px;
  background: 
    linear-gradient(
      135deg,
      rgba(10, 25, 45, 0.4) 0%,
      rgba(15, 35, 60, 0.3) 50%,
      rgba(10, 25, 45, 0.4) 100%
    ),
    rgba(0, 0, 0, 0.4);
  box-shadow: 
    0 20px 80px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(100, 182, 255, 0.15),
    inset 0 0 80px rgba(100, 182, 255, 0.03);
  backdrop-filter: blur(40px) saturate(150%);
  position: relative;
  overflow: hidden;
  animation: ${energyPulse} 6s ease-in-out infinite;
  transform-style: preserve-3d;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 5px 30px rgba(100, 182, 255, 0.3);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(100, 182, 255, 0.6) 25%,
      rgba(0, 255, 255, 0.8) 50%,
      rgba(100, 182, 255, 0.6) 75%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.6),
      0 -5px 30px rgba(100, 182, 255, 0.3);
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(100, 182, 255, 0.05) 30%,
    rgba(0, 255, 255, 0.08) 50%,
    rgba(100, 182, 255, 0.05) 70%,
    transparent 100%
  );
  animation: ${scanline} 8s ease-in-out infinite;
  pointer-events: none;
  z-index: 5;
  filter: blur(1px);
`;

const PartnersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  max-width: 1100px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const PartnerItem = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  gap: 40px;
  padding: 25px 45px;
  border: 1px solid rgba(100, 182, 255, 0.15);
  background: 
    linear-gradient(
      120deg,
      rgba(100, 182, 255, 0.03) 0%,
      rgba(0, 255, 255, 0.02) 50%,
      rgba(100, 182, 255, 0.03) 100%
    ),
    rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(20px);
  transform-style: preserve-3d;
  
  ${props => props.$visible && css`
    animation: ${fadeInSlide} 0.8s cubic-bezier(0.4, 0, 0.2, 1), ${floatAnimation} 8s ease-in-out infinite 0.8s;
  `}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(100, 182, 255, 0.8) 20%,
      rgba(0, 255, 255, 0.9) 50%,
      rgba(100, 182, 255, 0.8) 80%,
      transparent 100%
    );
    box-shadow: 
      0 0 20px rgba(100, 182, 255, 0.8),
      3px 0 30px rgba(100, 182, 255, 0.4);
    animation: ${glowPulse} 3s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(100, 182, 255, 0.15) 0%,
      rgba(0, 255, 255, 0.1) 50%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1), height 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover {
    transform: translateY(-5px) scale(1.02);
    border-color: rgba(100, 182, 255, 0.4);
    background: 
      linear-gradient(
        120deg,
        rgba(100, 182, 255, 0.08) 0%,
        rgba(0, 255, 255, 0.06) 50%,
        rgba(100, 182, 255, 0.08) 100%
      ),
      rgba(0, 0, 0, 0.3);
    box-shadow: 
      0 20px 60px rgba(100, 182, 255, 0.25),
      0 0 80px rgba(100, 182, 255, 0.15),
      inset 0 0 40px rgba(100, 182, 255, 0.08);
    
    &::after {
      width: 600px;
      height: 600px;
    }
  }
`;

const DataFlowLine = styled.div`
  position: absolute;
  top: 50%;
  left: 10px;
  width: 3px;
  height: 40px;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(100, 182, 255, 0.8) 30%,
    rgba(0, 255, 255, 1) 50%,
    rgba(100, 182, 255, 0.8) 70%,
    transparent 100%
  );
  border-radius: 50px;
  animation: ${dataFlow} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  box-shadow: 0 0 15px rgba(100, 182, 255, 0.8);
  filter: blur(0.5px);
`;

const PartnerLogoContainer = styled.div`
  flex-shrink: 0;
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 50%;
  padding: 20px;
  
  &::before {
    content: '';
    position: absolute;
    width: 110%;
    height: 110%;
    border: 2px solid rgba(100, 182, 255, 0.3);
    border-radius: 50%;
    animation: ${rotateGlow} 6s linear infinite;
    box-shadow: 0 0 20px rgba(100, 182, 255, 0.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 95%;
    height: 95%;
    border: 1px solid rgba(0, 255, 255, 0.2);
    border-radius: 50%;
    animation: ${glowPulse} 3s ease-in-out infinite;
  }
`;

const PartnerLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
  filter: 
    drop-shadow(0 0 15px rgba(100, 182, 255, 0.4))
    brightness(1.1)
    contrast(1.1);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${PartnerItem}:hover & {
    filter: 
      drop-shadow(0 0 25px rgba(100, 182, 255, 0.7))
      brightness(1.3)
      contrast(1.2);
    transform: scale(1.05);
  }
`;

const PartnerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
  padding: 5px 0;
`;

const PartnerName = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.2rem;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95),
    rgba(100, 182, 255, 0.9),
    rgba(255, 255, 255, 0.95)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  letter-spacing: 3px;
  text-shadow: 0 0 20px rgba(100, 182, 255, 0.4);
  font-weight: 300;
  position: relative;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 2px;
    background: linear-gradient(
      90deg,
      rgba(100, 182, 255, 0.8),
      rgba(0, 255, 255, 0.6),
      transparent
    );
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
    transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  ${PartnerItem}:hover & {
    letter-spacing: 4px;
    filter: drop-shadow(0 0 15px rgba(100, 182, 255, 0.6));
    
    &::after {
      width: 120px;
    }
  }
`;

const PartnerDescription = styled.p`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 8px 0 0 0;
  line-height: 1.8;
  letter-spacing: 1px;
  font-weight: 300;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${PartnerItem}:hover & {
    color: rgba(255, 255, 255, 0.8);
    letter-spacing: 1.5px;
  }
`;

const TechParticle = styled.div`
  position: absolute;
  width: 3px;
  height: 3px;
  background: radial-gradient(circle, #64B6FF, #00FFFF);
  border-radius: 50%;
  box-shadow: 
    0 0 15px rgba(100, 182, 255, 1),
    0 0 30px rgba(100, 182, 255, 0.5);
  animation: 
    ${floatAnimation} 4s ease-in-out infinite,
    ${glowPulse} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  top: ${props => props.$top}%;
  right: ${props => props.$right}%;
  opacity: 0.7;
  
  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: radial-gradient(circle, rgba(100, 182, 255, 0.3), transparent 70%);
    border-radius: 50%;
    animation: ${glowPulse} 2s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
  }
`;

const HolographicCorner = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  ${props => {
    switch(props.$position) {
      case 'top-left':
        return 'top: 0; left: 0; border-top: 2px solid; border-left: 2px solid;';
      case 'top-right':
        return 'top: 0; right: 0; border-top: 2px solid; border-right: 2px solid;';
      case 'bottom-left':
        return 'bottom: 0; left: 0; border-bottom: 2px solid; border-left: 2px solid;';
      case 'bottom-right':
        return 'bottom: 0; right: 0; border-bottom: 2px solid; border-right: 2px solid;';
      default:
        return '';
    }
  }}
  border-color: rgba(100, 182, 255, 0.4);
  animation: ${glowPulse} 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  pointer-events: none;
  z-index: 15;
  
  &::before {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background: radial-gradient(circle, rgba(100, 182, 255, 0.8), transparent);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.8);
    ${props => {
      switch(props.$position) {
        case 'top-left':
          return 'top: -3px; left: -3px;';
        case 'top-right':
          return 'top: -3px; right: -3px;';
        case 'bottom-left':
          return 'bottom: -3px; left: -3px;';
        case 'bottom-right':
          return 'bottom: -3px; right: -3px;';
        default:
          return '';
      }
    }}
  }
`;

const PresentationSlide11 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide11', lang);
  const [visibleCount, setVisibleCount] = useState(1);

  const partners = [
    {
      name: t.direcional.name,
      description: t.direcional.description,
      logo: '/direcional_logo.png'
    },
    {
      name: t.fyp.name,
      description: t.fyp.description,
      logo: '/fyp_logo.png'
    },
    {
      name: t.apolar.name,
      description: t.apolar.description,
      logo: '/logo-apolar.png'
    }
  ];

  const handleClick = () => {
    if (visibleCount < partners.length) {
      setVisibleCount(visibleCount + 1);
    }
  };

  return (
    <SlideWrapper onClick={handleClick}>
      <SlideTitle>{t.title}</SlideTitle>
      
      <ContentPreview>
        <ScanLine />
        
        <HolographicCorner $position="top-left" $delay={0} />
        <HolographicCorner $position="top-right" $delay={0.5} />
        <HolographicCorner $position="bottom-left" $delay={1} />
        <HolographicCorner $position="bottom-right" $delay={1.5} />
        
        <TechParticle $top={10} $right={5} $delay={0} />
        <TechParticle $top={20} $right={95} $delay={0.5} />
        <TechParticle $top={35} $right={8} $delay={0.8} />
        <TechParticle $top={45} $right={92} $delay={1.2} />
        <TechParticle $top={60} $right={5} $delay={1.6} />
        <TechParticle $top={75} $right={95} $delay={2} />
        <TechParticle $top={85} $right={10} $delay={2.4} />
        <TechParticle $top={25} $right={3} $delay={2.8} />
        <TechParticle $top={50} $right={97} $delay={3.2} />
        <TechParticle $top={15} $right={90} $delay={3.6} />
        
        <PartnersList>
          {partners.map((partner, index) => (
            <PartnerItem key={index} $index={index} $visible={index < visibleCount}>
              <PartnerLogoContainer>
                <PartnerLogo src={partner.logo} alt={partner.name} />
              </PartnerLogoContainer>
              
              <PartnerContent>
                <PartnerName>{partner.name}</PartnerName>
                {partner.description && (
                  <PartnerDescription>{partner.description}</PartnerDescription>
                )}
              </PartnerContent>
            </PartnerItem>
          ))}
        </PartnersList>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide11;
